<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import inboxesAPI from 'dashboard/api/inboxes';
import {
  extractNamedVariables,
  hasNamedVariables,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const isSaving = ref(false);
const isMenuOpen = ref(false);

const menuConfig = ref({
  enabled: false,
  button_text: 'Menu',
  commands: [],
});

// System variables that auto-fill per customer
const SYSTEM_VARIABLES = [
  'contact.name',
  'contact.first_name',
  'contact.last_name',
  'contact.email',
  'contact.phone_number',
  'agent.name',
  'agent.first_name',
  'agent.last_name',
  'agent.email',
  'conversation.id',
  'conversation.status',
  'inbox.name',
];

const cannedResponses = computed(() => {
  return store.getters['cannedResponse/getCannedResponses'];
});

// Get canned response content by ID
const getCannedResponseContent = responseId => {
  if (!responseId) return '';
  const response = cannedResponses.value.find(r => r.id === responseId);
  return response?.content || '';
};

// Detect custom variables in canned response (exclude system variables)
const getCustomVariables = content => {
  if (!content || !hasNamedVariables(content)) {
    return [];
  }

  const allVariables = extractNamedVariables(content);
  // Filter out system variables
  return allVariables.filter(variable => !SYSTEM_VARIABLES.includes(variable));
};

onMounted(async () => {
  // Load existing menu config from inbox
  if (props.inbox.menu_config) {
    menuConfig.value = { ...props.inbox.menu_config };
  }

  // Load canned responses from Vuex store
  try {
    await store.dispatch('cannedResponse/get', { searchKey: '' });
  } catch (error) {
    console.error('Failed to load canned responses:', error);
  }
});

const addCommand = () => {
  menuConfig.value.commands.push({
    command: '',
    description: '',
    canned_response_id: null,
    variables: {}, // Store custom variable values
  });
};

const removeCommand = index => {
  menuConfig.value.commands.splice(index, 1);
};

// Watch for canned response selection changes to update variables
watch(
  () => menuConfig.value.commands,
  newCommands => {
    newCommands.forEach(command => {
      if (command.canned_response_id) {
        const content = getCannedResponseContent(command.canned_response_id);
        const customVars = getCustomVariables(content);

        // Initialize variables object if not exists
        if (!command.variables) {
          command.variables = {};
        }

        // Remove variables that are no longer in the template
        Object.keys(command.variables).forEach(key => {
          if (!customVars.includes(key)) {
            delete command.variables[key];
          }
        });

        // Add new variables with empty values
        customVars.forEach(varName => {
          if (!(varName in command.variables)) {
            command.variables[varName] = '';
          }
        });
      } else {
        // Clear variables if no canned response selected
        command.variables = {};
      }
    });
  },
  { deep: true }
);

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const saveMenuConfig = async () => {
  isSaving.value = true;

  try {
    await inboxesAPI.update(props.inbox.id, {
      channel: {
        menu_config: menuConfig.value,
      },
    });

    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.SAVE_SUCCESS'));
  } catch (error) {
    console.error('Failed to save menu config:', error);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex items-start justify-between">
      <div>
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.DESCRIPTION') }}
        </p>
      </div>

      <label class="flex items-center gap-2">
        <input
          v-model="menuConfig.enabled"
          type="checkbox"
          class="rounded border-slate-300 text-woot-500 focus:ring-woot-500"
        />
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.ENABLE_MENU') }}
        </span>
      </label>
    </div>

    <div
      v-if="menuConfig.enabled"
      class="grid grid-cols-1 lg:grid-cols-2 gap-6"
    >
      <!-- Live Preview -->
      <div class="order-2 lg:order-1">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.PREVIEW') }}
        </h4>

        <div
          class="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-4 shadow-xl"
        >
          <!-- Phone Frame -->
          <div
            class="bg-white dark:bg-slate-100 rounded-2xl overflow-hidden flex flex-col"
            style="height: 600px"
          >
            <!-- Telegram Header -->
            <div class="bg-[#517DA2] px-4 py-3 flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              >
                <svg
                  class="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                  />
                </svg>
              </div>
              <div class="flex-1">
                <div class="text-white font-medium">{{ inbox.name }}</div>
                <div class="text-white/70 text-xs">bot</div>
              </div>
            </div>

            <!-- Chat Area -->
            <div class="flex-1 bg-[#E4DDD4] p-4 overflow-y-auto">
              <div class="text-center text-slate-500 text-xs mb-4">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.PREVIEW_HINT') }}
              </div>
            </div>

            <!-- Menu Button Area -->
            <div
              class="bg-white border-t border-slate-200 p-3 flex-shrink-0 relative"
            >
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="flex-1 bg-slate-100 hover:bg-slate-200 rounded-lg px-4 py-2 text-sm text-slate-600 font-medium transition-colors"
                  @click="toggleMenu"
                >
                  {{ menuConfig.button_text }}
                </button>
                <button
                  class="w-10 h-10 flex items-center justify-center text-slate-400"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              <!-- Commands List Preview (shown when menu button is clicked) -->
              <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
              >
                <div
                  v-if="isMenuOpen && menuConfig.commands.length > 0"
                  class="absolute bottom-full left-0 right-0 mb-2 mx-3 bg-slate-50 rounded-lg p-2 border border-slate-200"
                >
                  <div class="text-xs font-medium text-slate-500 mb-2 px-2">
                    {{
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.AVAILABLE_COMMANDS'
                      )
                    }}
                  </div>
                  <div class="space-y-1 max-h-40 overflow-y-auto">
                    <button
                      v-for="(command, index) in menuConfig.commands"
                      :key="index"
                      class="w-full bg-white rounded px-2 py-1.5 text-sm hover:bg-slate-100 transition-colors text-left"
                      @click="isMenuOpen = false"
                    >
                      <div class="font-medium text-slate-900">
                        /{{ command.command }}
                      </div>
                      <div class="text-xs text-slate-500">
                        {{ command.description }}
                      </div>
                    </button>
                  </div>
                </div>
              </transition>

              <!-- Empty state when menu is opened but no commands -->
              <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
              >
                <div
                  v-if="isMenuOpen && menuConfig.commands.length === 0"
                  class="absolute bottom-full left-0 right-0 mb-2 mx-3 bg-slate-50 rounded-lg p-3 border border-slate-200"
                >
                  <div class="text-xs text-slate-500 text-center">
                    {{
                      $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.NO_COMMANDS')
                    }}
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu Builder -->
      <div class="order-1 lg:order-2 space-y-4">
        <!-- Button Text -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.BUTTON_TEXT') }}
          </label>
          <input
            v-model="menuConfig.button_text"
            type="text"
            class="w-full rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 focus:border-woot-500 focus:ring-woot-500"
            :placeholder="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.BUTTON_TEXT_PLACEHOLDER'
              )
            "
          />
        </div>

        <!-- Commands -->
        <div>
          <div class="flex items-center justify-between mb-3">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.COMMANDS') }}
            </label>
            <button
              class="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-900/10 rounded-lg transition-colors"
              @click="addCommand"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.ADD_COMMAND') }}
            </button>
          </div>

          <div class="space-y-3">
            <div
              v-for="(command, index) in menuConfig.commands"
              :key="index"
              class="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-3"
            >
              <div class="flex items-start justify-between">
                <h5
                  class="text-sm font-medium text-slate-900 dark:text-slate-100"
                >
                  {{
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.COMMAND_NUMBER',
                      { number: index + 1 }
                    )
                  }}
                </h5>
                <button
                  class="text-red-500 hover:text-red-600 p-1"
                  :title="
                    $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.REMOVE_COMMAND')
                  "
                  @click="removeCommand(index)"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div>
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
                >
                  {{
                    $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.COMMAND_NAME')
                  }}
                </label>
                <div class="flex items-center gap-2">
                  <span class="text-slate-500 dark:text-slate-400">/</span>
                  <input
                    v-model="command.command"
                    type="text"
                    class="flex-1 rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 text-sm focus:border-woot-500 focus:ring-woot-500"
                    :placeholder="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.COMMAND_NAME_PLACEHOLDER'
                      )
                    "
                  />
                </div>
              </div>

              <div>
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
                >
                  {{
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.COMMAND_DESCRIPTION'
                    )
                  }}
                </label>
                <input
                  v-model="command.description"
                  type="text"
                  class="w-full rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 text-sm focus:border-woot-500 focus:ring-woot-500"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.COMMAND_DESCRIPTION_PLACEHOLDER'
                    )
                  "
                />
              </div>

              <div>
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
                >
                  {{
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.LINKED_RESPONSE'
                    )
                  }}
                  <span class="text-slate-400 font-normal">
                    ({{
                      $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.OPTIONAL')
                    }})
                  </span>
                </label>
                <select
                  v-model="command.canned_response_id"
                  class="w-full rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 text-sm focus:border-woot-500 focus:ring-woot-500"
                >
                  <option :value="null">
                    {{
                      $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.NO_RESPONSE')
                    }}
                  </option>
                  <option
                    v-for="response in cannedResponses"
                    :key="response.id"
                    :value="response.id"
                  >
                    {{ response.short_code }}
                  </option>
                </select>
              </div>

              <!-- Custom Variable Inputs -->
              <div
                v-if="
                  command.canned_response_id &&
                  getCustomVariables(
                    getCannedResponseContent(command.canned_response_id)
                  ).length > 0
                "
                class="mt-3 space-y-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
              >
                <div class="flex items-start gap-2 mb-2">
                  <svg
                    class="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <p
                    class="text-xs text-amber-800 dark:text-amber-300 font-medium"
                  >
                    {{
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.VARIABLES_DETECTED'
                      )
                    }}
                  </p>
                </div>
                <div
                  v-for="varName in getCustomVariables(
                    getCannedResponseContent(command.canned_response_id)
                  )"
                  :key="varName"
                  class="space-y-1"
                >
                  <label
                    class="block text-xs font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ varName }}
                  </label>
                  <input
                    v-model="command.variables[varName]"
                    type="text"
                    class="w-full rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 text-sm focus:border-woot-500 focus:ring-woot-500"
                    :placeholder="`Value for {{${varName}}}`"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="menuConfig.commands.length === 0"
              class="text-center py-8 text-slate-500 dark:text-slate-400 text-sm"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.NO_COMMANDS') }}
            </div>
          </div>
        </div>

        <!-- Save Button -->
        <div
          class="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-700"
        >
          <button
            :disabled="isSaving"
            class="px-4 py-2 bg-woot-500 hover:bg-woot-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            @click="saveMenuConfig"
          >
            <span v-if="isSaving">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.SAVING') }}
            </span>
            <span v-else>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_MENU.SAVE') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
